var kind      = require("enyo/kind"),
    Component = require("enyo/Component"),
    throttle  = require("./utils").throttle,
    leavePerformanceMode;

/**
 * The un-throttled PerformanceMode.leavePerformanceMode function.
 *
 * @private
 */
leavePerformanceMode = function() {
    if (this.counter === 0) {
        this.set("on", false);
    }
};

/**
 * The [PerformanceMode]{@link enyo-perf.PerformanceMode} singleton serves as an
 * application-wide indicator if the application is currently in a very
 * performance-sensitive state.
 *
 * If the [on]{@link enyo-perf.PerformanceMode#on} flag is set to `true`, this
 * means one or more parts of the application are trying to execute performance
 * sensitive tasks. This can usually be interpreted as a need for the
 * application to maintain butter-smooth 60fps animations.
 *
 * @class   enyo-perf.PerformanceMode
 * @extends enyo.Component
 * @public
 */
module.exports = kind.singleton(/** @lends enyo-perf.PerformanceMode.prototype */ {

    /**
     * @private
     */
    name: "enyo-perf.PerformanceMode",

    /**
     * @private
     */
    kind: Component,

    /**
     * This counter keeps track of the amount of times we have currently asked
     * to enable 'performance mode'.
     *
     * @type     {number}
     * @memberof enyo-perf.PerformanceMode
     * @readonly
     * @private
     */
    counter: 0,

    /**
     * Flags if 'performance mode' is currently on or not.
     *
     * @type     {boolean}
     * @memberof enyo-perf.PerformanceMode
     * @readonly
     * @public
     */
    on: false,

    /**
     * Throttle timeout for disabling of performance mode.
     *
     * @type     {number}
     * @memberof enyo-perf.PerformanceMode
     * @public
     */
    throttleTimeout: 50,

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.throttleTimeoutChanged();
        };
    }),

    /**
     * Enables performance mode by incrementing the counter.
     *
     * @memberof enyo-perf.PerformanceMode
     * @public
     */
    enable: function() {
        this.set("counter", this.counter + 1);
    },

    /**
     * Decrements the counter, thus possibly disabling performance mode.
     *
     * Decrementing is explicitly deferred to the end of the current stack, this
     * makes it easy to remain in performance mode if multiple
     * performance-sensitive tasks should be executed in one continuos series.
     *
     * @memberof enyo-perf.PerformanceMode
     * @public
     */
    disable: function() {
        var that = this;

        setTimeout(function() {
            that.set("counter", that.counter - 1);
        }, 0);
    },

    /**
     * Update the performanceMode [flag]{@link enyo-perf.PerformanceMode#on}
     * when the [counter]{@link enyo-perf.PerformanceMode#counter} changes.
     *
     * @memberof enyo-perf.PerformanceMode
     * @private
     */
    counterChanged: function() {
        if (this.counter > 0) {
            !this.on && this.enterPerformanceMode();
        }
        else if (this.counter === 0) {
            this.on && this.leavePerformanceMode();
        }
        else {
            this.error("PerformanceMode was disabled more often than it was enabled");
        }
    },

    /**
     * Turns performance mode on. This method should never be called directly,
     * use [enable]{@link enyo-perf.PerformanceMode#enable} instead.
     *
     * @memberof enyo-perf.PerformanceMode
     * @private
     */
    enterPerformanceMode: function() {
        this.set("on", true);
    },

    /**
     * Turns performance mode off. This method should never be called directly,
     * use [disable]{@link enyo-perf.PerformanceMode#disable} instead.
     *
     * This method will be throttled, so we need to check if performance mode
     * has not been enabled while disabling was deferred.
     *
     * @memberof enyo-perf.PerformanceMode
     * @name     leavePerformanceMode
     * @private
     */

    /**
     * @memberof enyo-perf.PerformanceMode
     * @private
     */
    throttleTimeoutChanged: function() {
        this.leavePerformanceMode = throttle(this.bindSafely(leavePerformanceMode), this.throttleTimeout);
    }

});
